import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import type { Chapter, CheatItem } from '../types';
import { renderMd } from '../md';

export function Cheatsheet({ chapters }: { chapters: Chapter[] }) {
  const [items, setItems] = useState<CheatItem[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<'all' | number>('all');

  const reload = () => api.listCheatsheet().then(setItems);
  useEffect(() => {
    reload();
  }, []);

  const grouped = useMemo(() => {
    const filtered =
      filter === 'all' ? items : items.filter((i) => i.chapter_id === filter);
    const map = new Map<number | null, CheatItem[]>();
    for (const it of filtered) {
      const key = it.chapter_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(it);
    }
    return map;
  }, [items, filter]);

  const create = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setBusy(true);
    try {
      await api.createCheat({
        chapter_id: fd.get('chapter_id') ? Number(fd.get('chapter_id')) : null,
        category: (fd.get('category') as string) || null,
        content_md: String(fd.get('content_md')),
        position: Number(fd.get('position') ?? 0),
      });
      e.currentTarget.reset();
      setOpen(false);
      reload();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex-between">
        <h2 style={{ margin: 0 }}>Cheatsheet</h2>
        <div className="row">
          <select
            value={String(filter)}
            onChange={(e) =>
              setFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))
            }
          >
            <option value="all">All chapters</option>
            {chapters.map((c) => (
              <option key={c.id} value={c.id}>
                Ch.{c.number} {c.title}
              </option>
            ))}
          </select>
          <button className="btn" onClick={() => setOpen((v) => !v)}>
            {open ? 'Close' : '＋ Add entry'}
          </button>
          <button className="btn secondary" onClick={() => window.print()}>
            🖨 Print
          </button>
        </div>
      </div>

      {open && (
        <form onSubmit={create} className="card col">
          <select name="chapter_id" defaultValue="">
            <option value="">— No chapter —</option>
            {chapters.map((c) => (
              <option key={c.id} value={c.id}>
                Ch.{c.number} {c.title}
              </option>
            ))}
          </select>
          <input name="category" placeholder="Category (e.g. Formula, Definition)" />
          <input
            name="position"
            type="number"
            defaultValue={0}
            placeholder="Sort position"
          />
          <textarea
            name="content_md"
            placeholder="Markdown content (formulas, definitions, mnemonics…)"
            required
          />
          <div>
            <button className="btn" disabled={busy}>
              {busy ? 'Saving…' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {[...grouped.entries()].map(([chapterId, list]) => {
        const chapter = chapters.find((c) => c.id === chapterId);
        return (
          <section key={String(chapterId)} className="card">
            <h3 style={{ marginTop: 0 }}>
              {chapter ? `Ch.${chapter.number} — ${chapter.title}` : 'Misc'}
            </h3>
            {list.map((it) => (
              <div key={it.id} style={{ marginBottom: 12 }}>
                <div className="flex-between">
                  {it.category && <div className="muted">{it.category}</div>}
                  <button
                    className="btn danger"
                    onClick={async () => {
                      if (!confirm('Delete entry?')) return;
                      await api.deleteCheat(it.id);
                      reload();
                    }}
                  >
                    Delete
                  </button>
                </div>
                <div
                  className="markdown"
                  dangerouslySetInnerHTML={{ __html: renderMd(it.content_md) }}
                />
              </div>
            ))}
          </section>
        );
      })}

      {!items.length && (
        <div className="muted">No cheatsheet entries yet.</div>
      )}
    </div>
  );
}
